import moment from 'moment';
import { DEFAULT_PLAYER_NAME, EventProfileTabIdentifiers, MatchStatus, PositionNumbers, StatusText } from './constants';
import { drawEliminationTypeIds, drawPositionTypeIds } from './eventConstants';
import { shouldDisplayRoundNumbersV2 } from './events';
import { formatMatchDateForEventPage, formatMatchTime } from './formatting';
import { getPlayerName, getTiebreak } from './index';
import {
    CompletionTypes,
    DefaultedLabel,
    DoubleWalkoverLabel,
    ForfeitValues,
    OutComeTypes,
    RetiredValues,
    WithdrewLabel,
} from './postScoreConstants';
import { getValueByTypeId, showTagValue } from './types';
import { getDoublesPlayerDisplayNameOfMatch, TBD_DISPLAY } from './courtSchedular';
import { participatingMemberToPlayerObject } from './participatingMemberUtils';

// Format player name based on match status
// Full name for upcoming, Initial. Last for live/completed/suspended
export const formatPlayerNameByStatus = (player, shortenFirstName) => {
    if (!player) {
        return DEFAULT_PLAYER_NAME;
    }

    return getPlayerName(player.firstName, player.lastName, !!shortenFirstName);
};

export const isMatchUpcoming = (match) => {
    const hasResult = match.result?.score;
    const isLive = match?.isLive;

    return !hasResult && !isLive;
};

export const isMatchLive = (match) => {
    const hasResult = match.result?.score;
    const isLive = match?.isLive;

    return !hasResult && isLive;
};

export const isMatchCompleted = (match) => {
    const hasResult = match.result?.score;
    const endedWithScores =
        !match?.result?.score &&
        !match?.isLive &&
        !!match.liveMatch?.periodScores?.length &&
        match.liveMatch?.matchDurationSeconds != null;
    return !!hasResult || endedWithScores;
};

export const getMatchDurationSeconds = (match) => {
    return match?.liveMatch?.matchDurationSeconds ?? match?.matchDurationSeconds ?? null;
};

export const getScheduleInfoForMatch = (oopScheduleMatch) => {
    return {
        matchId: oopScheduleMatch.matchId,
        courtId: oopScheduleMatch.courtId,
        includesTime: oopScheduleMatch.includesTime,
        notBefore: oopScheduleMatch.notBefore,
        sequenceNumber: oopScheduleMatch.sequenceNumber,
    };
};

const getUpcomingStatusText = (scheduleInfo, matchToLastTimedMatchMap, date, timeZoneValue) => {
    const { includesTime, notBefore } = scheduleInfo;
    const hasPreviousTimedMatch = !!matchToLastTimedMatchMap[scheduleInfo.matchId];
    const formattedTime = date ? formatMatchTime(date, timeZoneValue) : null;

    let statusText = '';
    if (includesTime && !notBefore && date) {
        statusText = `${StatusText.STARTS_AT} ${formattedTime}`;
    } else if (date && notBefore) {
        if (hasPreviousTimedMatch) {
            statusText = `${StatusText.FOLLOWED_BY}, ${StatusText.NOT_BEFORE} ${formattedTime}`;
        } else {
            statusText = `${StatusText.NOT_BEFORE} ${formattedTime}`;
        }
    } else if (hasPreviousTimedMatch) {
        statusText = `${StatusText.FOLLOWED_BY}`;
    } else {
        statusText = StatusText.TBD;
    }

    return statusText;
};

const toStatusResult = (opts) => ({
    statusBarText: opts.statusBarText ?? null,
    shouldShowDate: opts.shouldShowDate ?? opts.shouldShowDateAndTime ?? true,
    shouldShowTime: opts.shouldShowTime ?? opts.shouldShowDateAndTime ?? true,
    matchDate: opts.matchDate ?? null,
    matchStartTime: opts.matchStartTime ?? null,
    matchDuration: opts.matchDuration ?? null,
    className: opts.className ?? 'dark',
    ...opts,
});

const buildMatchContext = (match, timeZoneValue, scheduleInfo) => {
    const { date } = match;
    const isForScheduleTab = !!scheduleInfo;
    const formattedDate = date ? formatMatchDateForEventPage(date, timeZoneValue, true) : null;
    const formattedTime = date ? formatMatchTime(date, timeZoneValue) : null;
    const includesTime = scheduleInfo?.includesTime ?? match?.includesTime;
    const notBefore = scheduleInfo?.notBefore ?? match?.notBefore;

    return { date, formattedDate, formattedTime, isForScheduleTab, includesTime, notBefore, timeZoneValue };
};

const isPendingResult = (match) => {
    const result = match?.result;
    return result?.score &&
        result.finalized === false &&
        (result.playerPostedResult === true || (result.players?.winner1 != null && result.players?.loser1 != null));
};

// Resolve best available date, format it, fall back to ctx formatted values.
const resolveFormattedDate = (preferredDate, ctx) => {
    const { formattedDate, formattedTime, timeZoneValue } = ctx;
    return {
        matchDate: preferredDate ? formatMatchDateForEventPage(preferredDate, timeZoneValue, true) : formattedDate,
        matchStartTime: preferredDate ? formatMatchTime(preferredDate, timeZoneValue) : formattedTime,
    };
};

const getLiveOrSuspendedStatus = (match, ctx) => {
    const isSuspended = match.liveMatch?.status === MatchStatus.SUSPENDED || match.liveMatch?.status === MatchStatus.INTERRUPTED;
    // DE-2692: ctx.date later than startTime = rescheduled after going live; show it until completion.
    const startTime = match.liveMatch?.startTime;
    const isRescheduled = !!ctx.date && !!startTime && moment.utc(ctx.date).isAfter(moment.utc(startTime));
    const liveDate = isRescheduled ? ctx.date : (startTime || ctx.date);

    return {
        status: isSuspended ? MatchStatus.SUSPENDED : MatchStatus.LIVE,
        statusBarText: isSuspended ? StatusText.SUSPENDED : StatusText.LIVE,
        shouldShowDate: !ctx.isForScheduleTab,
        shouldShowTime: true,
        matchDate: liveDate ? formatMatchDateForEventPage(liveDate, ctx.timeZoneValue, true) : null,
        matchStartTime: liveDate ? formatMatchTime(liveDate, ctx.timeZoneValue) : null,
        matchDuration: formatMatchDuration(getMatchDurationSeconds(match)),
        className: isSuspended ? null : 'green',
    };
};

const getPendingStatus = (match, ctx) => {
    const pendingDate = ctx.date ?? match?.result?.date;

    return {
        status: MatchStatus.PENDING,
        statusBarText: StatusText.PENDING,
        shouldShowDate: !ctx.isForScheduleTab,
        shouldShowTime: true,
        ...resolveFormattedDate(pendingDate, ctx),
    };
};

const getCompletedStatus = (match, ctx) => {
    const completedDate = match.liveMatch?.startTime || ctx.date || match.result?.date;

    return {
        status: MatchStatus.COMPLETED,
        statusBarText: StatusText.COMPLETED,
        shouldShowDate: !ctx.isForScheduleTab,
        shouldShowTime: true,
        ...resolveFormattedDate(completedDate, ctx),
        matchDuration: formatMatchDuration(getMatchDurationSeconds(match)),
    };
};

const getScheduleTabStatus = (ctx, scheduleInfo, matchToLastTimedMatchMap) => {
    const { date, formattedDate, formattedTime, timeZoneValue } = ctx;

    return {
        status: MatchStatus.UPCOMING,
        statusBarText: getUpcomingStatusText(scheduleInfo, matchToLastTimedMatchMap, date, timeZoneValue),
        shouldShowDate: false,
        shouldShowTime: false,
        matchDate: formattedDate,
        matchStartTime: formattedTime,
        matchDuration: null,
        className: 'dark',
    };
};

const getUpcomingStatus = (match, ctx) => {
    const { date, formattedDate, formattedTime, includesTime, notBefore } = ctx;

    if (date && includesTime && !notBefore) {
        return toStatusResult({
            status: MatchStatus.STARTS_AT,
            statusBarText: `Starts at ${formattedTime}`,
            shouldShowDateAndTime: true,
            matchDate: formattedDate,
            matchStartTime: formattedTime,
        });
    }
    if (date && notBefore) {
        return toStatusResult({
            status: MatchStatus.NOT_BEFORE,
            statusBarText: `Not before ${formattedTime}`,
            shouldShowDateAndTime: true,
            matchDate: formattedDate,
            matchStartTime: formattedTime,
        });
    }
    if (date && includesTime === false) {
        return toStatusResult({
            status: MatchStatus.UPCOMING,
            statusBarText: null,
            shouldShowDate: true,
            shouldShowTime: false,
            matchDate: formattedDate,
            matchStartTime: formattedTime,
        });
    }

    const hasNoScheduledTime = !date;
    const hasBye = match?.position1?.typeId === drawPositionTypeIds.BYE || match?.position2?.typeId === drawPositionTypeIds.BYE;
    if (hasBye && hasNoScheduledTime) {
        return {
            status: MatchStatus.UPCOMING,
            statusBarText: null,
            shouldShowDate: false,
            shouldShowTime: false,
            matchDate: null,
            matchStartTime: null,
            matchDuration: null,
        };
    }
    return {
        status: MatchStatus.UPCOMING,
        statusBarText: hasNoScheduledTime ? StatusText.TBD : null,
        shouldShowDate: !hasNoScheduledTime,
        shouldShowTime: !hasNoScheduledTime,
        matchDate: formattedDate,
        matchStartTime: formattedTime,
        matchDuration: null,
        className: 'dark',
    };
};

export const getMatchStatusData = (match, timeZoneValue, scheduleInfo = null, matchToLastTimedMatchMap = {}, isLiveScoresEnabled = false) => {
    const ctx = buildMatchContext(match, timeZoneValue, scheduleInfo);

    const isSuspendedOrInterrupted =
        match.liveMatch?.status === MatchStatus.SUSPENDED ||
        match.liveMatch?.status === MatchStatus.INTERRUPTED;

    if (isLiveScoresEnabled && (isMatchLive(match) || isSuspendedOrInterrupted)) {
        return getLiveOrSuspendedStatus(match, ctx);
    }

    if (isPendingResult(match)) {
        return getPendingStatus(match, ctx);
    }

    if (isMatchCompleted(match)) {
        return getCompletedStatus(match, ctx);
    }

    if (ctx.isForScheduleTab) {
        return getScheduleTabStatus(ctx, scheduleInfo, matchToLastTimedMatchMap);
    }

    return getUpcomingStatus(match, ctx);
};

export const getStatusBarDisplayItems = (matchStatusData, _match, showMatchDate = true, showTimeBased = false) => {
    const {
        statusBarText,
        shouldShowDate,
        shouldShowTime,
        matchDate,
        status,
    } = matchStatusData;

    const items = [];
    const activeMatchStatuses = [MatchStatus.LIVE, MatchStatus.COMPLETED, MatchStatus.SUSPENDED, MatchStatus.PENDING];

    const showStatusText = statusBarText && (
        !showMatchDate ||
        activeMatchStatuses.includes(status) ||
        statusBarText === StatusText.TBD
    );
    if (showStatusText) {
        items.push({
            key: 'status-item-1',
            type: 'text',
            value: statusBarText,
            className: 'status',
        });
    }

    if (showMatchDate) {
        if (shouldShowDate && matchDate) {
            items.push({
                key: 'status-item-2',
                type: 'text',
                value: matchDate,
                className: 'dateTime',
            });
        }
        if (shouldShowTime && matchStatusData?.matchStartTime) {
            items.push({
                key: 'status-item-3',
                type: 'text',
                value: matchStatusData.matchStartTime,
                className: 'dateTime',
            });
        }
    } else if (activeMatchStatuses.includes(status) && matchStatusData?.matchStartTime) {
        items.push({
            key: 'status-item-3',
            type: 'text',
            value: matchStatusData.matchStartTime,
            className: 'dateTime',
        });
    } else {
        // no need to handle
    }

    if (showTimeBased) {
        items.push({
            key: 'status-item-time-based',
            type: 'image',
            src: '/img/v4/icon-clock.svg',
            title: 'This match uses timed based scoring',
            className: 'timeBasedIcon',
        });
    }

    return items;
};

/**
 * Returns display items for the match card data bar (draw name, round, facility, court).
 * Each item: { key, type: 'text' | 'link', value, className, href? }.
 * Builds the draw name link from event + draw when both ids are present.
 */
export const getDataBarDisplayItems = (match, roundLabel, options = {}) => {
    const {
        showDrawName = false,
        showFacilityCourtInfo = false,
        drawDisplayName = null,
        event = null,
        draw = null,
    } = options;

    const items = [];
    const drawHref = (event?.id && draw?.id)
        ? `/events/${event.id}?t=${EventProfileTabIdentifiers.DRAWS}&d=${draw.id}`
        : null;

    if (showDrawName && drawDisplayName) {
        items.push({
            key: 'data-bar-draw-name',
            type: drawHref ? 'link' : 'text',
            value: drawDisplayName,
            className: 'groupName',
            ...(drawHref && { href: drawHref }),
        });
    }

    items.push({
        key: 'data-bar-round-label',
        type: 'text',
        value: roundLabel?.label ?? '',
        className: 'roundName',
    });

    if (roundLabel?.match != null) {
        items.push({
            key: 'data-bar-round-match',
            type: 'text',
            value: roundLabel.match,
            className: 'roundName',
        });
    }

    if (showFacilityCourtInfo && match?.facilityAbbreviation) {
        items.push({
            key: 'data-bar-facility',
            type: 'text',
            value: match.facilityAbbreviation,
            className: 'facilityName',
        });
    }

    if (showFacilityCourtInfo && match?.courtName) {
        items.push({
            key: 'data-bar-court',
            type: 'text',
            value: match.courtName,
            className: 'courtName',
        });
    }

    return items;
};

// Get round label for match card (draws tab) - Returns { label, match }
export const getRoundLabelForMatchCard = (eliminationTypeId, roundNumber, roundSize, matchPosition) => {
    const matchText = matchPosition != null ? `Match ${matchPosition}` : null;
    if (eliminationTypeId === drawEliminationTypeIds.MATCH_PLAY) {
        return { label: matchPosition != null ? `Match ${matchPosition}` : 'Match', match: null };
    }
    if (shouldDisplayRoundNumbersV2(eliminationTypeId)) {
        return { label: `R${roundNumber}`, match: null };
    }
    if (roundSize === 1) {
        return { label: 'F', match: null };
    }
    if (roundSize === 2) {
        return { label: 'SF', match: matchText };
    }
    if (roundSize === 4) {
        return { label: 'QF', match: matchText };
    }
    if (roundSize != null && roundSize > 0) {
        return { label: `R${roundSize * 2}`, match: matchText };
    }
    return { label: roundNumber != null ? `R${roundNumber}` : 'R', match: matchText };
};

export const getLiveScoreDisplay = (gameState, liveScoreKey) => {
    if (!gameState) {
        return null;
    }
    const home = gameState.home_score;
    const away = gameState.away_score;
    const isDeuce = Number(home) >= 40 && Number(away) >= 40;
    if (isDeuce && gameState.advantage === 'home' && liveScoreKey === 'home_score') {
        return 'AD';
    }
    if (isDeuce && gameState.advantage === 'away' && liveScoreKey === 'away_score') {
        return 'AD';
    }
    const raw = gameState[liveScoreKey];

    return raw != null ? raw : null;
};

// Counts sets won per side from periodScores.
// Note: tied periods (h === a) count toward away — matches existing behavior.
const computePeriodScoreWinners = (periodScores) => {
    let homeSetsWon = 0;
    let awaySetsWon = 0;
    periodScores.forEach((p) => {
        const h = parseInt(p.home_score ?? p.homeScore ?? 0, 10);
        const a = parseInt(p.away_score ?? p.awayScore ?? 0, 10);
        if (h > a) {
            homeSetsWon += 1;
        } else {
            awaySetsWon += 1;
        }
    });
    return [homeSetsWon > awaySetsWon, awaySetsWon > homeSetsWon];
};

// Get player scores per set - Array of score objects or null
// When live or just-ended with retained liveMatch: use liveMatch.periodScores (home = position1, away = position2).
// Otherwise: use result.score (winner/loser mapped via didPosition1Win/didPosition2Win).
export const getScoresForPosition = (match, positionNumber, isLiveScoresEnabled = false) => {
    const shouldUseLiveMatchData = isLiveScoresEnabled && (isMatchLive(match) || match.liveMatch != null);
    if (shouldUseLiveMatchData && match.liveMatch?.periodScores?.length) {
        const { periodScores } = match.liveMatch;
        const isLive = isMatchLive(match);
        let didPosition1WinMatch = false;
        if (!isLive) {
            [didPosition1WinMatch] = computePeriodScoreWinners(periodScores);
        }
        return periodScores.map((period, index) => {
            const isPosition1 = positionNumber === PositionNumbers.POSITION_1;
            const homeScore = period.home_score ?? period.homeScore;
            const awayScore = period.away_score ?? period.awayScore;
            const playerScore = isPosition1 ? homeScore : awayScore;
            const opponentScore = isPosition1 ? awayScore : homeScore;
            const isHigher = parseInt(playerScore, 10) > parseInt(opponentScore, 10);
            const homeTiebreak = period.home_tiebreak_score ?? period.homeTiebreakScore;
            const awayTiebreak = period.away_tiebreak_score ?? period.awayTiebreakScore;
            const periodTiebreak = period.tiebreak_score ?? period.tiebreakScore;
            const tiebreakValue = isPosition1 ? (homeTiebreak ?? periodTiebreak) : (awayTiebreak ?? periodTiebreak);
            const showTiebreakSup = tiebreakValue != null && tiebreakValue > 0;
            const isMatchWinner = isLive ? false : (isPosition1 && didPosition1WinMatch) || (!isPosition1 && !didPosition1WinMatch);
            return {
                id: `score-${positionNumber}-${index}`,
                score: playerScore,
                isWinner: isMatchWinner,
                isHigher,
                tiebreak: null,
                tiebreakValue: showTiebreakSup ? tiebreakValue : null,
            };
        });
    }

    const score = match.result?.score || {};
    const scoreArray = Object.values(score);
    if (scoreArray.length === 0) {
        return null;
    }

    const [didPosition1Win, didPosition2Win] = getMatchWinnerPositions(match, isLiveScoresEnabled, true);

    return scoreArray.map((scoreItem) => {
        const isPosition1 = positionNumber === PositionNumbers.POSITION_1;
        const pos1Score = didPosition1Win ? scoreItem.winner : scoreItem.loser;
        const pos2Score = didPosition2Win ? scoreItem.winner : scoreItem.loser;
        const playerScore = isPosition1 ? pos1Score : pos2Score;
        const pos1Opponent = didPosition1Win ? scoreItem.loser : scoreItem.winner;
        const pos2Opponent = didPosition2Win ? scoreItem.loser : scoreItem.winner;
        const opponentScore = isPosition1 ? pos1Opponent : pos2Opponent;
        const isHigher = parseInt(playerScore, 10) > parseInt(opponentScore, 10);
        const isMatchWinner = (isPosition1 && didPosition1Win) || (!isPosition1 && didPosition2Win);
        const tiebreak = getTiebreak(scoreItem);
        const tiebreakValue = isMatchWinner ? scoreItem.winnerTiebreak : scoreItem.tiebreak;
        const showTiebreakSup = tiebreakValue != null && tiebreakValue >= 0;

        return {
            score: playerScore,
            isWinner: isMatchWinner,
            isHigher,
            tiebreak,
            tiebreakValue: showTiebreakSup ? tiebreakValue : null,
        };
    });
};

// Whether the user can post score in the event profile (schedule tab).
// Considers event reg date (maxPostScoreDateUtc); can be extended for event type, etc.
export const canPostScoreInEventProfile = (event) => {
    const maxPostScoreDateUtc = event?.eventSchedule?.maxPostScoreDateUtc;
    if (!maxPostScoreDateUtc) {
        return false;
    }
    return moment.utc().isBefore(moment(maxPostScoreDateUtc));
};

// Format match duration from live/ended match payload (matchDurationSeconds).
// Returns e.g. "50 Min" or "1:14 Min" (1 hr 14 min), or null if invalid.
export const formatMatchDuration = (totalSeconds) => {
    if (totalSeconds == null || totalSeconds < 0) {
        return null;
    }
    const totalMins = Math.floor(totalSeconds / 60);
    if (totalMins < 60) {
        return `${totalMins} Min`;
    }
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return `${hours}:${String(mins).padStart(2, '0')} Min`;
};

// Get match outcome label for display
// Returns e.g. 'Withdrew', 'Abandoned', 'Defaulted', 'Incomplete', 'Double Walkover', 'Retired', 'Pending', 'Forfeit', 'Tie', or null.
export const getMatchOutcomeLabel = (result) => {
    if (!result) {
        return null;
    }
    const outcome = result.outcome?.toLowerCase() ?? '';
    const completionType = result.completionType?.toLowerCase() ?? '';
    if (result.playerPostedResult && !result.finalized) {
        return 'Pending';
    }

    if (ForfeitValues.includes(outcome)) {
        return 'Forfeit';
    }

    if (outcome === OutComeTypes.COMPLETED || completionType === CompletionTypes.WINNER_COMPLETED) {
        return null;
    }

    if (outcome === OutComeTypes.INCOMPLETE || completionType === CompletionTypes.TIE_INCOMPLETE) {
        return 'Incomplete';
    }

    if (completionType === CompletionTypes.TIE_COMPLETED) {
        return 'Tie';
    }

    if (outcome === OutComeTypes.ABANDONED) {
        return 'Abandoned';
    }

    if (outcome === OutComeTypes.DOUBLEWALKOVER) {
        return DoubleWalkoverLabel;
    }

    if (outcome === OutComeTypes.DEFAULTED) {
        return DefaultedLabel;
    }

    if (outcome === OutComeTypes.WITHDREW) {
        return WithdrewLabel;
    }

    if (RetiredValues.includes(outcome) || completionType === CompletionTypes.WINNER_INCOMPLETE) {
        return 'Retired';
    }

    return null;
};

// Build players lookup by member id from participatingMembers
export const getParticipantsByMemberId = (participatingMembers = []) => {
    return participatingMembers.reduce((acc, member) => {
        if (member.id != null) {
            acc[member.id] = participatingMemberToPlayerObject(member);
        }
        return acc;
    }, {});
};

/**
 * Returns a display string for a draw (e.g. "Parent - Draw", "Draw, Group", or "Draw").
 * Returns null if draw has no name.
 */
export const getDrawDisplayName = (draw) => {
    if (!draw?.name) {
        return null;
    }
    if (draw.parentDrawName && draw.parentDrawName !== draw.name) {
        return `${draw.parentDrawName} - ${draw.name}`;
    }
    if (draw.groupName) {
        return `${draw.name}, ${draw.groupName}`;
    }
    return draw.name;
};

// For building pairs to show Flex League contacts popup
export const getContactPairsByPosition = (position1MemberIds, position2MemberIds, participatingMembers = []) => {
    const lookup = getParticipantsByMemberId(participatingMembers);
    const memberToContactEntry = (member) => ({
        key: member?.memberId,
        name: member?.displayName || getPlayerName(member?.firstName, member?.lastName, false) || '',
        phone: member?.phone,
        email: member?.email,
        homeCourtName: member?.homeCourtName,
    });
    const toEntries = (ids) => (ids || [])
        .map((id) => lookup[id])
        .filter(Boolean)
        .map((member) => memberToContactEntry(member));
    const pos1Entries = toEntries(position1MemberIds);
    const pos2Entries = toEntries(position2MemberIds);
    const groups = [pos1Entries, pos2Entries].filter((group) => group.length > 0);

    if (pos1Entries.length === 1 && pos2Entries.length === 1) {
        return [[...pos1Entries, ...pos2Entries]];
    }
    return groups;
};

/**
 * Get array of player objects for one position (singles: 1 item, doubles: 2 items).
 * TBD or empty position: 1 placeholder with default name. Bye: 1 item with name 'Bye'.
 * Uses memberIds array: singles [12345], doubles [12345, 12346]. Same code path for both formats.
 */
export const getPositionPlayers = (matchDetails, positionNumber, participatingMembers = []) => {
    const position = positionNumber === 1 ? matchDetails?.position1 : matchDetails?.position2;
    const participantsByMemberId = getParticipantsByMemberId(participatingMembers);

    const tbdPlaceholder = { name: DEFAULT_PLAYER_NAME, nationality: null, player: null, memberId: null };

    if (!position) {
        return [tbdPlaceholder];
    }

    if (position.typeId === drawPositionTypeIds.BYE) {
        return [{ name: 'Bye', nationality: null, player: null, memberId: null, isTeam: false }];
    }

    const memberIds = position.memberIds ?? [];

    if (memberIds.length === 0) {
        return [tbdPlaceholder];
    }

    return memberIds.map((memberId) => {
        const player = participantsByMemberId[memberId];
        if (player) {
            const nationality = player.nationality ?? player.countryCode ?? player.country ?? null;
            return {
                name: getPlayerName(player.firstName, player.lastName, false) || DEFAULT_PLAYER_NAME,
                nationality,
                player,
                memberId,
            };
        }
        return { name: DEFAULT_PLAYER_NAME, nationality: null, player: null, memberId };
    });
};

// returns [didPosition1Win, didPosition2Win] from result.players.winner1 and positions.
// No position is treated as winner when there is no score or outcome is Abandoned/Double Walkover (no bold).
// For score mapping (winner/loser per set), pass forScoreMapping=true so abandoned matches still get correct set scores.
export const getMatchWinnerPositions = (matchDetails, isLiveScoresEnabled = false, forScoreMapping = false) => {
    let didPosition1Win = false;
    let didPosition2Win = false;
    if (isLiveScoresEnabled && isMatchLive(matchDetails)) {
        return [didPosition1Win, didPosition2Win];
    }

    // Ended from SignalR with only liveMatch.periodScores (no result yet)
    const periodScores = matchDetails?.liveMatch?.periodScores;
    if (!matchDetails?.isLive && periodScores?.length && !matchDetails?.result?.score) {
        [didPosition1Win, didPosition2Win] = computePeriodScoreWinners(periodScores);
        return [didPosition1Win, didPosition2Win];
    }

    const result = matchDetails?.result;
    const hasScore = !!result?.score;
    const isAbandonedOrDoubleWalkover = result && (
        result.outcome === OutComeTypes.ABANDONED || result.outcome === OutComeTypes.DOUBLEWALKOVER
    );
    if (!hasScore || (isAbandonedOrDoubleWalkover && !forScoreMapping)) {
        return [didPosition1Win, didPosition2Win];
    }

    const winner1 = result?.players?.winner1;
    if (winner1 == null) {
        return [didPosition1Win, didPosition2Win];
    }

    const participants = matchDetails.participatingMembers || [];
    const memberIdPlayerIdRelationMap = participants.reduce((acc, m) => { acc[m.id] = m.playerId; return acc; }, {});

    const pos1 = matchDetails.position1;
    const pos2 = matchDetails.position2;
    const pos1MemberIds = pos1?.memberIds ?? [];
    const pos2MemberIds = pos2?.memberIds ?? [];

    const pos1PlayerIds = pos1MemberIds.map((mid) => memberIdPlayerIdRelationMap[mid]).filter(Boolean);
    const pos2PlayerIds = pos2MemberIds.map((mid) => memberIdPlayerIdRelationMap[mid]).filter(Boolean);

    if (pos1PlayerIds.includes(winner1)) {
        didPosition1Win = true;
    }
    if (pos2PlayerIds.includes(winner1)) {
        didPosition2Win = true;
    }
    return [didPosition1Win, didPosition2Win];
};

// Player tag (Q, LL, WC, Alt) from schedule API position.tagId
export const getPlayerTag = (position, drawTagTypes) => {
    if (!position || !drawTagTypes?.length) {
        return null;
    }

    const { tagId } = position;
    if (!showTagValue(tagId)) {
        return null;
    }

    return getValueByTypeId(drawTagTypes, tagId) || null;
};

export const getMatchSheetTeamDisplayName = (position, participatingMembers = []) => {
    const memberIds = position?.memberIds ?? [];
    if (memberIds.length === 0) {
        return TBD_DISPLAY;
    }
    return memberIds
        .map((_, i) => getDoublesPlayerDisplayNameOfMatch(position, participatingMembers, i))
        .join(' / ');
};

// Returns null when either member has no rating. Treats 0 the same as null/undefined
// because the v2 endpoint returns doublesUtr: 0 for unrated players, and we want the
// rating area to render blank (matching pre-change v1 behavior, which read
// team.averageDoublesUtr — null for unrated teams).
export const getMatchSheetTeamAvgDoublesUtr = (position, participatingMembers = []) => {
    const memberIds = position?.memberIds ?? [];
    if (memberIds.length < 2) {
        return null;
    }
    const [id1, id2] = memberIds;
    const m1 = participatingMembers.find(m => m.id === id1);
    const m2 = participatingMembers.find(m => m.id === id2);
    if (!m1?.doublesUtr || !m2?.doublesUtr) {
        return null;
    }
    return ((m1.doublesUtr + m2.doublesUtr) / 2).toFixed(2);
};
